import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { blogsData } from '../data/mock';

const Blog = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    const handlePopState = () => {
      setSelectedBlog(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const openBlogDetail = (blog) => {
    setSelectedBlog(blog);
    window.history.pushState({ blogModal: true }, '');
  };

  const closeBlogDetail = () => {
    if (selectedBlog) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        setSelectedBlog(null);
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <>
      <section id="blog" className="py-24 bg-[#121212]" ref={ref}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Design Insights
            </h2>
            <p
              className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Explore our latest thoughts on design, architecture, and creating beautiful spaces
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {blogsData.map((blog) => (
              <motion.article
                key={blog.id}
                variants={cardVariants}
                whileHover={{ y: -10 }}
                className="bg-[#1a1a1a] overflow-hidden group cursor-pointer"
                onClick={() => openBlogDetail(blog)}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-2 bg-[#e8d5c4] text-black text-xs font-semibold uppercase tracking-wide">
                      {blog.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center space-x-4 mb-4 text-white/50 text-sm">
                    <div className="flex items-center">
                      <Calendar size={16} className="mr-2" />
                      <span>{blog.date}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock size={16} className="mr-2" />
                      <span>{blog.readTime}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3
                    className="text-2xl font-bold text-white mb-3 group-hover:text-[#e8d5c4] transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {blog.title}
                  </h3>

                  {/* Excerpt */}
                  <p
                    className="text-white/70 mb-4 line-clamp-3"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {blog.excerpt}
                  </p>

                  {/* Read More */}
                  <button
                    className="text-[#e8d5c4] font-semibold uppercase tracking-wide text-sm group-hover:gap-3 flex items-center gap-2 transition-all"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Read More
                    <span className="group-hover:translate-x-2 transition-transform">→</span>
                  </button>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Blog Detail Modal */}
      <AnimatePresence>
        {selectedBlog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
            onClick={closeBlogDetail}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-[#0a0a0a] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Blog Image */}
              <div className="relative h-96 overflow-hidden">
                <img
                  src={selectedBlog.image}
                  alt={selectedBlog.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent"></div>
              </div>

              {/* Blog Content */}
              <div className="p-8 md:p-12">
                <button
                  onClick={closeBlogDetail}
                  className="mb-6 inline-flex items-center gap-2 px-4 py-2 border border-white/30 text-white hover:bg-white/10 transition-all"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  aria-label="Go back to previous page"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-4 mb-6 text-white/50">
                  <span className="px-4 py-2 bg-[#e8d5c4] text-black text-xs font-semibold uppercase tracking-wide">
                    {selectedBlog.category}
                  </span>
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-2" />
                    <span>{selectedBlog.date}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock size={16} className="mr-2" />
                    <span>{selectedBlog.readTime}</span>
                  </div>
                </div>

                {/* Title */}
                <h1
                  className="text-4xl md:text-5xl font-bold text-white mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {selectedBlog.title}
                </h1>

                {/* Content */}
                <div
                  className="prose prose-invert prose-lg max-w-none"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Blog;
